import { useEffect, useState } from 'react';
import { Box, Chip, Divider, Pagination, Paper, Stack, Typography } from '@mui/material';
import Iconify from 'src/components/iconify';
import CustomTooltip from './CustomTooltip';
import SerpModalButton from './SerpModalButton';
import { PlaItem } from '../types';

interface Props {
    items: PlaItem[];
    columns?: number;
    pageSize?: number;
    showBrandChip?: boolean;
    totalCount?: number;
}

export default function RightPanelAds({ items, columns = 2, pageSize = 10, showBrandChip = false, totalCount }: Props) {
    const [page, setPage] = useState(1);
    useEffect(() => { setPage(1); }, [items, columns]);
    const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
    const start = (page - 1) * pageSize;
    const current = items.slice(start, start + pageSize);
    const displayCount = totalCount !== undefined ? totalCount : items.length;
    return (
        <Stack spacing={1.5} >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2">Right panel Ads</Typography>
                </Stack>
                <Typography variant="caption" fontSize={14} color="text.secondary">{displayCount} results</Typography>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 1 }}>
                {current.map((ad, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, backgroundColor: 'background.paper', position: 'relative' }}>
                        <Box sx={{ position: 'absolute', top: 6, right: 6 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                {showBrandChip && (((ad as any).brandInfringementTitle) || ((ad as any).brandInfringementDescription)) && (
                                    <Chip label="Brand" color="error" size="small" sx={{ height: 16 }} />
                                )}
                                <CustomTooltip title={`search term: ${ad.searchTerm}`} placement="top">
                                    <Iconify icon="mdi:information-outline" width={16} height={16} sx={{ opacity: 0.8 }} />
                                </CustomTooltip>
                            </Stack>
                        </Box>
                        <Stack spacing={0.75}>
                            <Box sx={{ height: 60, width: 60, borderRadius: '0', bgcolor: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                {ad.img ? <Box component="img" src={ad.img} alt={ad.title} sx={{ width: '100%', height: '100%', objectFit: 'contain', transformOrigin: 'center center' }} /> : <Iconify icon="mdi:image-outline" fontSize="small" />}
                            </Box>
                            <Box sx={{ 
                                position: 'relative',
                                ...(showBrandChip && ((ad as any).brandInfringementTitle || (ad as any).brandInfringementDescription) && {
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        width: '120px',
                                        height: '16px',
                                        background: 'linear-gradient(90deg, transparent, background.paper 40%)',
                                        pointerEvents: 'none'
                                    }
                                })
                            }}>
                                <Typography variant="caption" sx={{ lineClamp: 3, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                    {ad.title}
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 400, color: 'text.secondary' }}>{ad.price}</Typography>
                            <Stack direction="row" spacing={0.5} display="flex" flexDirection="column" flexWrap="wrap">
                                <Divider sx={{ my: 0.5 }} />
                                <Stack direction="row" justifyContent="space-between" spacing={0.5}>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Position</Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>{`#${ad.position}`}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={0.5}>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Frequency</Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>{`${ad.frequency}%`}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={0.5}>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>SERP</Typography>
                                    <Stack direction="row" spacing={0.5}>
                                        <SerpModalButton
                                            gcsUrl={(ad as any)._gcs_uri_mobile}
                                            deviceType={ad.device_type}
                                            iconType="mobile"
                                            searchTerm={ad.searchTerm}
                                        />
                                        <SerpModalButton
                                            gcsUrl={(ad as any)._gcs_uri_desktop}
                                            deviceType={ad.device_type}
                                            iconType="desktop"
                                            searchTerm={ad.searchTerm}
                                        />
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Paper>
                ))}
            </Box>
            {pageCount > 1 && (
                <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
                    <Pagination color="standard" size="small" count={pageCount} page={page} onChange={(_, p) => setPage(p)} />
                </Stack>
            )}
        </Stack>
    );
}


